import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../api/client";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ExternalLink, Building2, Globe, MapPin, ShieldCheck } from "lucide-react";
import { getFullImageUrl } from "../../utils/imageHandler";
import { glassCard, glassPanel } from "../../utils/glassStyles";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import Loader from "../../components/ui/Loader";

export default function RecruiterProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/recruiter/profile");
        if (res.data) setProfile(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (loading) return <Loader label="Loading profile..." />;

  if (!profile) {
    return (
      <RecruiterPageShell title="Recruiter Profile" onBack={() => navigate("/recruiter/dashboard")}>
        <p className="text-muted-foreground text-center py-16">Recruiter profile not found.</p>
      </RecruiterPageShell>
    );
  }

  return (
    <RecruiterPageShell
      title="Company Profile"
      subtitle="Your public company information on Shebeka"
      onBack={() => navigate("/recruiter/dashboard")}
      actions={
        <>
          <Button onClick={() => navigate("/recruiter/profile/edit")}>Edit Profile</Button>
          {profile.id && profile.isApproved && (
            <Button asChild variant="outline">
              <Link to={`/companies/${profile.id}`}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Public Page
              </Link>
            </Button>
          )}
        </>
      }
    >
      <div className="max-w-3xl mx-auto space-y-6">
        <Card className={`rounded-2xl overflow-hidden ${glassCard}`}>
          <CardHeader className="border-b border-white/10 pb-4">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Building2 className="w-5 h-5 text-primary" />
              Company Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
              <div className="w-28 h-28 rounded-2xl border border-white/20 bg-white/30 dark:bg-white/10 overflow-hidden flex items-center justify-center shrink-0">
                {profile.logoUrl ? (
                  <img
                    src={getFullImageUrl(profile.logoUrl)}
                    alt={profile.companyName}
                    className="w-full h-full object-contain p-2"
                  />
                ) : (
                  <span className="text-3xl font-bold text-primary/60">
                    {profile.companyName?.[0]?.toUpperCase() || "?"}
                  </span>
                )}
              </div>
              <div className="flex-1 text-center sm:text-left space-y-2">
                <h2 className="text-2xl font-bold">{profile.companyName}</h2>
                <Badge
                  variant={profile.isApproved ? "default" : "secondary"}
                  className={profile.isApproved ? "bg-primary/15 text-primary border-primary/30" : ""}
                >
                  <ShieldCheck className="w-3 h-3 mr-1" />
                  {profile.isApproved ? "Verified Recruiter" : "Pending Verification"}
                </Badge>
              </div>
            </div>

            <div className={`rounded-xl ${glassPanel} space-y-4 !p-5`}>
              <Detail label="About" value={profile.about || "—"} />
              {profile.publicNote && <Detail label="Public Note" value={profile.publicNote} />}
              {profile.location && (
                <Detail
                  label="Location"
                  value={
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-primary" />
                      {profile.location}
                    </span>
                  }
                />
              )}
              {profile.website && (
                <Detail
                  label="Website"
                  value={
                    <a
                      href={profile.website.startsWith("http") ? profile.website : `https://${profile.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-1.5"
                    >
                      <Globe className="w-4 h-4" />
                      {profile.website.replace(/^https?:\/\//, "")}
                    </a>
                  }
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </RecruiterPageShell>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      <div className="text-sm sm:text-base text-foreground leading-relaxed whitespace-pre-wrap">{value}</div>
    </div>
  );
}
